import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft,
  User,
  Phone,
  Calendar,
  Mail,
  LogOut,
  Save,
  Loader2,
  Gift,
  Trophy,
  MapPin,
  Gamepad2,
  Target,
  Award,
  Camera,
  Home,
  CheckCircle,
  Sparkles,
  Tag,
  Copy,
  ChevronRight,
  UserCircle,
  Star
} from 'lucide-react';
import CustomerFooter from '../../components/CustomerFooter';

import { BACKEND_URL, API } from '../../utils/apiConfig';

// Default avatars based on gender
const DEFAULT_AVATARS = {
  Male: 'https://api.dicebear.com/7.x/avataaars/svg?seed=male&backgroundColor=b6e3f4&style=circle',
  Female: 'https://api.dicebear.com/7.x/avataaars/svg?seed=female&backgroundColor=ffdfbf&style=circle',
  Other: 'https://api.dicebear.com/7.x/avataaars/svg?seed=neutral&backgroundColor=c0aede&style=circle',
  default: 'https://api.dicebear.com/7.x/avataaars/svg?seed=default&backgroundColor=d1d4f9&style=circle'
};

const getDefaultAvatar = (gender) => DEFAULT_AVATARS[gender] || DEFAULT_AVATARS.default;

// Profile field configuration with colors
const PROFILE_FIELDS = [
  { key: 'name', label: 'Full Name', icon: User, type: 'text', placeholder: 'Enter your full name', color: 'emerald', gradient: 'from-emerald-400 to-teal-500' },
  { key: 'date_of_birth', label: 'Date of Birth', icon: Calendar, type: 'date', placeholder: '', color: 'blue', gradient: 'from-blue-400 to-cyan-500' },
  { key: 'gender', label: 'Gender', icon: UserCircle, type: 'select', options: ['Male', 'Female', 'Other'], color: 'purple', gradient: 'from-purple-400 to-pink-500' },
  { key: 'most_played_game', label: 'Most Played Game', icon: Gamepad2, type: 'select', options: ['Badminton', 'Pickleball', 'Tennis', 'Squash'], color: 'orange', gradient: 'from-orange-400 to-amber-500' },
  { key: 'email', label: 'Email ID', icon: Mail, type: 'email', placeholder: 'your@email.com', color: 'rose', gradient: 'from-rose-400 to-red-500' },
  { key: 'skill_level', label: 'Skill Level', icon: Target, type: 'select', options: ['Beginner', 'Intermediate', 'Advanced', 'Professional'], color: 'indigo', gradient: 'from-indigo-400 to-violet-500' },
  { key: 'dupr_id', label: 'DUPR ID', icon: Award, type: 'text', placeholder: 'e.g., 12345678', color: 'amber', gradient: 'from-amber-400 to-yellow-500' },
  { key: 'preferred_venue', label: 'Preferred Venue', icon: MapPin, type: 'venue-select', color: 'cyan', gradient: 'from-cyan-400 to-sky-500' },
  { key: 'profile_photo', label: 'Profile Photo', icon: Camera, type: 'photo-upload', color: 'pink', gradient: 'from-pink-400 to-fuchsia-500' },
  { key: 'short_address', label: 'Short Address', icon: Home, type: 'text', placeholder: 'e.g., Andheri West, Mumbai', color: 'teal', gradient: 'from-teal-400 to-green-500' },
];

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-200', ring: 'focus:ring-emerald-500' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600', border: 'border-blue-200', ring: 'focus:ring-blue-500' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600', border: 'border-purple-200', ring: 'focus:ring-purple-500' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-600', border: 'border-orange-200', ring: 'focus:ring-orange-500' },
  rose: { bg: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-200', ring: 'focus:ring-rose-500' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600', border: 'border-indigo-200', ring: 'focus:ring-indigo-500' },
  amber: { bg: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-200', ring: 'focus:ring-amber-500' },
  cyan: { bg: 'bg-cyan-100', text: 'text-cyan-600', border: 'border-cyan-200', ring: 'focus:ring-cyan-500' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-600', border: 'border-pink-200', ring: 'focus:ring-pink-500' },
  teal: { bg: 'bg-teal-100', text: 'text-teal-600', border: 'border-teal-200', ring: 'focus:ring-teal-500' },
};

export default function CustomerProfilePage() {
  const { customer, setCustomer, logout } = useCustomerAuth();
  const navigate = useNavigate();
  
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [venues, setVenues] = useState([]);
  const [formData, setFormData] = useState({});
  const [showRewardModal, setShowRewardModal] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    fetchProfile();
    fetchVenues();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      setFormData({
        name: response.data.name || '',
        date_of_birth: response.data.date_of_birth || '',
        gender: response.data.gender || '',
        most_played_game: response.data.most_played_game || '',
        email: response.data.email || '',
        skill_level: response.data.skill_level || '',
        dupr_id: response.data.dupr_id || '',
        preferred_venue: response.data.preferred_venue || '',
        profile_photo: response.data.profile_photo || '',
        short_address: response.data.short_address || '',
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/user/venues?game=Badminton`);
      setVenues(response.data.venues || []);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    }
  };

  const handleChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or WebP image');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingPhoto(true);
    
    try {
      const token = localStorage.getItem('customer_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/user/profile/photo`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update local state with new photo URL
      const newPhotoUrl = response.data.photo_url;
      setFormData(prev => ({ ...prev, profile_photo: newPhotoUrl }));
      setProfileData(prev => ({ ...prev, profile_photo: newPhotoUrl }));
      
      toast.success('Photo uploaded successfully! 📸');
    } catch (error) {
      console.error('Photo upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const token = localStorage.getItem('customer_token');
      const response = await axios.put(`${API}/user/profile`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Update local state
      setProfileData(response.data.profile);
      
      // Update context safely
      if (setCustomer && response.data.profile) {
        setCustomer(response.data.profile);
      }
      
      // Check if reward was unlocked
      if (response.data.reward_unlocked) {
        setShowRewardModal(true);
      } else {
        toast.success('Profile saved successfully! ✨');
      }
      
      setEditing(false);
    } catch (error) {
      console.error('Profile update error:', error);
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to update profile';
      if (detail) {
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail[0]?.msg || detail[0]?.message || 'Validation error';
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || 'Validation error';
        }
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const copyCode = () => {
    navigator.clipboard.writeText('PROFILE100');
    toast.success('Coupon code copied!');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Calculate max date (user must be at least 5 years old)
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() - 5);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const completionPercentage = profileData?.profile_completion || 0;
  const missingFields = profileData?.missing_fields || [];
  const rewardClaimed = profileData?.profile_reward_claimed || false;

  const getFieldValue = (key) => {
    if (key === 'preferred_venue') {
      const venue = venues.find(v => v.id === profileData?.[key]);
      return venue?.name || profileData?.[key] || 'Not set';
    }
    return profileData?.[key] || 'Not set';
  };

  const isFieldComplete = (key) => {
    return profileData?.[key] && String(profileData[key]).trim() !== '';
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-40 h-40 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-60 h-60 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="relative px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/')}
                className="p-2 -ml-2 rounded-xl hover:bg-white/10 transition-colors"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="font-bold text-lg">My Profile</h1>
            </div>
            {!editing && (
              <button
                onClick={() => setEditing(true)}
                className="px-4 py-1.5 text-sm font-medium bg-white/20 hover:bg-white/30 rounded-full transition-colors backdrop-blur-sm"
                data-testid="edit-btn"
              >
                Edit Profile
              </button>
            )}
          </div>
        </div>

        {/* Profile Avatar & Completion */}
        <div className="relative flex flex-col items-center pb-8 pt-2">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center mb-3 border-4 border-white/40 shadow-xl overflow-hidden">
              {profileData?.profile_photo ? (
                <img 
                  src={profileData.profile_photo} 
                  alt="Profile" 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : profileData?.gender ? (
                <img 
                  src={getDefaultAvatar(profileData.gender)} 
                  alt="Default Avatar" 
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-4xl font-bold text-white drop-shadow-lg">
                  {profileData?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              )}
            </div>
            {/* Completion badge */}
            <div className={`absolute -bottom-1 -right-1 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-lg ${
              completionPercentage >= 100 
                ? 'bg-gradient-to-br from-amber-300 to-yellow-500 text-amber-900' 
                : 'bg-white text-emerald-600'
            }`}>
              {completionPercentage}%
            </div>
          </div>
          <h2 className="text-xl font-bold mt-1">{profileData?.name || 'User'}</h2>
          <p className="text-sm text-white/80 flex items-center gap-1">
            <Phone className="w-3 h-3" />
            +91 {profileData?.mobile}
          </p>
        </div>
      </div>

      {/* Progress Bar & Nudge */}
      <div className="px-4 -mt-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100/50 p-5 mb-4">
          {/* Progress Bar */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              Profile Completion
            </span>
            <span className={`text-sm font-bold px-2 py-0.5 rounded-full ${
              completionPercentage >= 100 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-emerald-100 text-emerald-700'
            }`}>
              {completionPercentage}%
            </span>
          </div>
          <div className="w-full h-4 bg-slate-100 rounded-full overflow-hidden mb-3 shadow-inner">
            <div 
              className={`h-full transition-all duration-700 ease-out rounded-full relative overflow-hidden ${
                completionPercentage >= 100 
                  ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500' 
                  : 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-500'
              }`}
              style={{ width: `${completionPercentage}%` }}
            >
              {/* Animated shine effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
            </div>
          </div>

          {/* Nudge or Reward */}
          {completionPercentage < 100 ? (
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 rounded-xl border border-purple-100/50">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center flex-shrink-0 shadow-md">
                <Gift className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-bold text-purple-800">
                  Complete your profile to unlock rewards 🎁
                </p>
                <p className="text-xs text-purple-600 mt-1">
                  Fill {missingFields.length} more field{missingFields.length > 1 ? 's' : ''} to get <span className="font-bold">₹100 off</span> coupon!
                </p>
              </div>
            </div>
          ) : rewardClaimed ? (
            <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 via-yellow-50 to-orange-50 rounded-xl border border-amber-200/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shadow-md">
                  <Trophy className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-amber-800">Reward Unlocked! 🎉</p>
                  <p className="text-xs text-amber-600">Code: <span className="font-mono font-bold bg-amber-100 px-2 py-0.5 rounded">PROFILE100</span></p>
                </div>
              </div>
              <button 
                onClick={copyCode}
                className="p-2.5 bg-amber-100 hover:bg-amber-200 rounded-xl transition-colors"
              >
                <Copy className="w-4 h-4 text-amber-700" />
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-2 sm:px-6">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100/50 overflow-hidden">
          {editing ? (
            // Edit Mode
            <div className="p-5 space-y-5">
              <div className="text-center mb-6">
                <h3 className="font-bold text-slate-800">Edit Your Profile</h3>
                <p className="text-sm text-slate-500">Complete all fields to unlock rewards</p>
              </div>
              
              {PROFILE_FIELDS.map((field) => {
                const colorClass = COLOR_CLASSES[field.color];
                return (
                  <div key={field.key} className="space-y-2">
                    <Label htmlFor={field.key} className="text-slate-700 font-medium flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-lg ${colorClass.bg} flex items-center justify-center`}>
                        <field.icon className={`w-3.5 h-3.5 ${colorClass.text}`} />
                      </div>
                      {field.label}
                      {!isFieldComplete(field.key) && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold">+10%</span>
                      )}
                    </Label>
                    
                    {field.type === 'select' ? (
                      <select
                        id={field.key}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className={`w-full h-12 px-4 border-2 ${colorClass.border} rounded-xl focus:outline-none focus:ring-2 ${colorClass.ring} bg-slate-50/50 transition-all`}
                      >
                        <option value="">Select {field.label}</option>
                        {field.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === 'venue-select' ? (
                      <select
                        id={field.key}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        className={`w-full h-12 px-4 border-2 ${colorClass.border} rounded-xl focus:outline-none focus:ring-2 ${colorClass.ring} bg-slate-50/50 transition-all`}
                      >
                        <option value="">Select Preferred Venue</option>
                        {venues.map(venue => (
                          <option key={venue.id} value={venue.id}>{venue.name}</option>
                        ))}
                      </select>
                    ) : field.type === 'photo-upload' ? (
                      <div className="space-y-3">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handlePhotoUpload}
                          className="hidden"
                          id="photo-upload-input"
                        />
                        <div className="flex items-center gap-3">
                          {/* Current Photo Preview */}
                          <div className="w-16 h-16 rounded-xl overflow-hidden border-2 border-slate-200 bg-slate-50 flex items-center justify-center">
                            {formData[field.key] ? (
                              <img 
                                src={formData[field.key]} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                              />
                            ) : profileData?.gender ? (
                              <img 
                                src={getDefaultAvatar(profileData.gender)} 
                                alt="Default" 
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Camera className="w-6 h-6 text-slate-300" />
                            )}
                          </div>
                          {/* Upload Button */}
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingPhoto}
                            className={`flex-1 h-12 px-4 border-2 border-dashed ${colorClass.border} rounded-xl flex items-center justify-center gap-2 hover:bg-pink-50 transition-colors ${uploadingPhoto ? 'opacity-50' : ''}`}
                          >
                            {uploadingPhoto ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin text-pink-500" />
                                <span className="text-sm text-pink-600">Uploading...</span>
                              </>
                            ) : (
                              <>
                                <Camera className="w-4 h-4 text-pink-500" />
                                <span className="text-sm text-pink-600 font-medium">
                                  {formData[field.key] ? 'Change Photo' : 'Upload Photo'}
                                </span>
                              </>
                            )}
                          </button>
                        </div>
                        <p className="text-xs text-slate-400">JPG, PNG or WebP. Max 5MB.</p>
                      </div>
                    ) : (
                      <Input
                        id={field.key}
                        type={field.type}
                        placeholder={field.placeholder}
                        value={formData[field.key] || ''}
                        onChange={(e) => handleChange(field.key, e.target.value)}
                        max={field.type === 'date' ? maxDateStr : undefined}
                        className={`h-12 border-2 ${colorClass.border} rounded-xl focus:ring-2 ${colorClass.ring} bg-slate-50/50`}
                      />
                    )}
                  </div>
                );
              })}

              <div className="flex gap-3 pt-4">
                <Button
                  onClick={() => {
                    setEditing(false);
                    fetchProfile();
                  }}
                  variant="outline"
                  className="flex-1 h-12 rounded-xl border-2"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="flex-1 h-12 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 rounded-xl shadow-lg shadow-emerald-500/25"
                  data-testid="save-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            // View Mode
            <div>
              {/* Mobile Number (always shown, not editable) */}
              <div className="flex items-center gap-4 p-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-md">
                  <Phone className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 font-medium">Mobile Number</p>
                  <p className="font-bold text-slate-900">+91 {profileData?.mobile}</p>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
              </div>

              {/* Profile Fields */}
              {PROFILE_FIELDS.map((field, index) => {
                const isComplete = isFieldComplete(field.key);
                const value = getFieldValue(field.key);
                const colorClass = COLOR_CLASSES[field.color];
                
                return (
                  <div 
                    key={field.key} 
                    className={`flex items-center gap-4 p-4 transition-colors hover:bg-slate-50 ${
                      index < PROFILE_FIELDS.length - 1 ? 'border-b border-slate-100' : ''
                    }`}
                    onClick={() => setEditing(true)}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${
                      isComplete 
                        ? `bg-gradient-to-br ${field.gradient}` 
                        : 'bg-slate-100'
                    }`}>
                      <field.icon className={`w-5 h-5 ${isComplete ? 'text-white' : 'text-slate-400'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-slate-500 font-medium">{field.label}</p>
                      <p className={`font-semibold truncate ${isComplete ? 'text-slate-900' : 'text-slate-400 italic'}`}>
                        {field.key === 'date_of_birth' ? formatDate(profileData?.[field.key]) : value}
                      </p>
                    </div>
                    {isComplete ? (
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-500" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] px-2 py-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold">
                          +10%
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-300" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Logout Button */}
        {!editing && (
          <button
            onClick={handleLogout}
            className="w-full mt-4 flex items-center justify-center gap-2 py-3.5 px-4 bg-white rounded-xl border border-slate-200 text-red-500 font-semibold hover:bg-red-50 hover:border-red-200 transition-all shadow-sm"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Sign Out
          </button>
        )}
      </div>
      
      {/* Reward Unlocked Modal */}
      {showRewardModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-sm w-full p-6 text-center animate-in zoom-in-95 shadow-2xl">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-300 via-yellow-400 to-orange-400 flex items-center justify-center mx-auto mb-4 shadow-xl shadow-amber-500/30 relative">
              <Trophy className="w-12 h-12 text-white drop-shadow-lg" />
              <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent to-white/20"></div>
            </div>
            <div className="flex items-center justify-center gap-1 mb-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              <h2 className="text-2xl font-black text-slate-900">Congratulations!</h2>
              <Sparkles className="w-5 h-5 text-amber-500" />
            </div>
            <p className="text-slate-600 mb-4">
              You unlocked a <span className="font-bold text-emerald-600">₹100 discount</span> coupon!
            </p>
            
            <div className="bg-gradient-to-r from-emerald-50 via-teal-50 to-cyan-50 rounded-2xl p-5 mb-5 border-2 border-dashed border-emerald-300">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Tag className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-emerald-600 font-bold uppercase tracking-wider">Your Coupon Code</span>
              </div>
              <div className="flex items-center justify-center gap-3">
                <span className="text-3xl font-mono font-black text-emerald-700 tracking-wider">PROFILE100</span>
                <button onClick={copyCode} className="p-2 bg-emerald-100 hover:bg-emerald-200 rounded-lg transition-colors">
                  <Copy className="w-5 h-5 text-emerald-600" />
                </button>
              </div>
            </div>
            
            <Button
              onClick={() => {
                setShowRewardModal(false);
                navigate('/');
              }}
              className="w-full h-14 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600 hover:from-emerald-600 hover:via-teal-600 hover:to-cyan-700 text-white font-bold text-lg rounded-2xl shadow-lg shadow-emerald-500/25"
              data-testid="reward-lets-book-btn"
            >
              Awesome! Let's Book 🎾
            </Button>
          </div>
        </div>
      )}

      {/* CSS for shimmer animation */}
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
      
      {/* Footer */}
      <CustomerFooter />
    </div>
  );
}
